import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../lib/auth';

interface FeatureFlags {
  [key: string]: boolean;
}

const FeatureFlagContext = createContext<FeatureFlags>({});

interface FeatureFlagProviderProps {
  children: ReactNode;
  initialFlags?: FeatureFlags;
}

export const FeatureFlagProvider = ({ children, initialFlags }: FeatureFlagProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlags>(initialFlags || {});

  useEffect(() => {
    // Always fetch unless we have initialFlags
    if (initialFlags === undefined || initialFlags === null) {
      // Delay fetching until we have a token
      const fetchFlags = () => {
        const token = authService.getAccessToken();

        // Add timestamp to prevent caching
        const url = `/api/feature-flags/?t=${Date.now()}`;

        fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
          },
          cache: 'no-store'
        })
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          setFlags(data);
        })
        .catch(() => {
          // Unauthenticated requests get a 401 here (no token yet) — fall back
          // to an empty flag set rather than surfacing a console error.
          setFlags({});
        });
      };

      // Try to fetch immediately, but also retry after a short delay if no token
      const token = authService.getAccessToken();
      if (token) {
        fetchFlags();
      } else {
        // Wait a bit for auth to complete, then fetch
        setTimeout(fetchFlags, 1000);
      }
    }
  }, []); // Remove dependency on initialFlags

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = (flag: string): boolean => {
  const flags = useContext(FeatureFlagContext);
  const value = Boolean(flags[flag]);
  return value;
};

/**
 * Hook specifically for checking if scheduling is enabled
 */
export const useSchedulingEnabled = () => {
  const flags = useContext(FeatureFlagContext);
  return { data: Boolean(flags['scheduling_enabled']) };
};

/**
 * Hook specifically for checking if automations is enabled
 */
export const useAutomationsEnabled = () => {
  const flags = useContext(FeatureFlagContext);
  return { data: Boolean(flags['automations_enabled']) };
};
